/**
 * speech_budget — who may speak aloud, and when (2026-09-15, the versona
 * review's speech budget). One synthesised voice at a time on this client:
 *   direct   the conversation you opened (T) — replaces anything playing
 *   warning  a shouted warning — replaces ambient talk, never a conversation
 *   ambient  overheard chatter — speaks only into SILENCE: nothing live, a gap
 *            of AMBIENT_GAP_MS since the last ambient line, the speaker within
 *            AMBIENT_RANGE_M (the distance the bubble is legible from), the
 *            line short. A square murmurs; it never shouts over your talk.
 * Pure — the harness pins it.
 */

export type SpeechPriority = 'direct' | 'warning' | 'ambient';
export const PRIORITY_RANK: Record<SpeechPriority, number> = { direct: 3, warning: 2, ambient: 1 };
/** Silence between two ambient lines (ms). */
export const AMBIENT_GAP_MS = 3000;
/** An overheard line is voiced only this close (m) — CHATTER_FULL_M, where the bubble reads. */
export const AMBIENT_RANGE_M = 11;
/** Longer lines stay text — a paragraph of synthesis for a passer-by is not worth the wait. */
export const AMBIENT_MAX_CHARS = 120;

/** May `incoming` start while an utterance of `liveRank` (0 = none) is live? */
export function interruptAllowed(liveRank: number, incoming: SpeechPriority): boolean {
  if (liveRank === 0) return true;
  if (incoming === 'ambient') return false;
  return PRIORITY_RANK[incoming] >= liveRank;
}

export type AmbientVerdict = 'ok' | 'busy' | 'gap' | 'far' | 'long';

/** The ambient rung's own gate (interruptAllowed decides against live speech first). */
export function ambientVerdict(liveRank: number, lastAmbientEndMs: number, nowMs: number, distM: number, textLen: number): AmbientVerdict {
  if (liveRank > 0) return 'busy';
  if (nowMs - lastAmbientEndMs < AMBIENT_GAP_MS) return 'gap';
  if (distM > AMBIENT_RANGE_M) return 'far';
  if (textLen > AMBIENT_MAX_CHARS) return 'long';
  return 'ok';
}
